import { useState, useRef } from 'react';
import IValidatable from '../../Shared/validatable.interface';
import ValidationInfo from '../../../validation/validation-info';
import HoneyPot, { IHoneyPot } from '../../Shared/HoneyPot';
import { ValidatableComponent, ValidationHelper } from '../../../helpers/validation.helper';
import { AnalyticsService } from '../../../services/analytics-service';
import { IServerValidationError } from '../../Shared/validatable.interface';
import { TranslationService } from '../../../services/translation.service';
import { Status } from '../../../hooks/fetch.hook';
import SelectFormGroup from '../../Shared/SelectFormGroup';
import ValidationSummary from '../../Shared/ValidationSummary';
import TextFieldFormGroup from '../../Shared/TextFieldFormGroup';
import { SanitizerService } from '../../../services/sanitizer.service';
import AntiforgeryService from '../../../services/anti-forgery.service';
import Alert from '../../Shared/Alert';

const unsubscribeReasons = [
  { value: 'notworkingwithpublictransport', text: 'Jag jobbar inte längre med kollektivtrafik.' },
  { value: 'notinteresting', text: 'Tidningens innehåll är inte tillräckligt intressant.' },
  { value: 'noanswer', text: 'Vill inte svara' },
];

function StralkastarenUnregistration() {
  // === STATE ===
  const [emailState, setEmailState] = useState<{ value: string; validationInfo: ValidationInfo }>({
    value: '',
    validationInfo: new ValidationInfo(),
  });
  const [reasonState, setReasonState] = useState<{ value: string; validationInfo: ValidationInfo }>({
    value: '',
    validationInfo: new ValidationInfo(),
  });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // === REFS ===
  const emailRef = useRef<IValidatable>(null!);
  const reasonRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.StralkastarenUnregistration, AnalyticsService.ErrorAction, e);
    });
  }

  function focusValidationSummary(): void {
    setTimeout(() => {
      const validationSummary = document.getElementById('private-individual-unregistration-form-validation-summary');
      if (validationSummary) {
        validationSummary.focus();
        validationSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    if (submitStatus === Status.loading) return;

    setSubmitStatus(Status.notperformed);
    setShowErrorAlert(false);

    if (!honeyPotRef.current.isValid()) return;

    const [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());

    if (!isFormValid) {
      ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
        AnalyticsService.addEvent(AnalyticsService.StralkastarenUnregistration, AnalyticsService.ErrorAction, e);
      });
      focusValidationSummary();
      return;
    }
    try {
      setSubmitStatus(Status.loading);

      const response = await fetch(`/api/stralkastaren/unregistration/private-individual/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        body: JSON.stringify(emailState.value.trim()),
      });
      if (response.ok) {
        setSubmitStatus(Status.success);
        AnalyticsService.addEvent(AnalyticsService.StralkastarenUnregistration, 'Skickat avregistrering - privatperson', unsubscribeReasons.find((r) => r.value === reasonState.value)?.text || '');
      } else {
        setSubmitStatus(Status.failed);
        if (response.status === 400) {
          handleServerValidationErrors((await response.json()).errors);
          focusValidationSummary();
        } else {
          setShowErrorAlert(true);
          AnalyticsService.addEvent(AnalyticsService.StralkastarenUnregistration, 'Skickat avregistrering - privatperson - misslyckat', '');
        }
      }
    } catch {
      setSubmitStatus(Status.failed);
      setShowErrorAlert(true);
    }
  }

  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: emailRef.current, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: reasonRef.current, validationInfo: reasonState.validationInfo, setState: setReasonState },
    ];
  }

  return (
    <div>
      <ValidationSummary id="private-individual-unregistration-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />

      <div className="private-individual-unregistration-form" hidden={submitStatus === Status.success}>
        <TextFieldFormGroup
          ref={emailRef}
          required={true}
          labelText={TranslationService.get('common.email')}
          inputId="personal-identity-email"
          serverValidationName="email"
          type="email"
          autocomplete="off"
          hideRequiredAsterisk={true}
          state={emailState}
          onChange={(value) => {
            setEmailState((ps) => ({ ...ps, value, validationInfo: new ValidationInfo() }));
          }}
        />
        <SelectFormGroup
          ref={reasonRef}
          labelText="Anledning"
          id="reason"
          className="mb-3"
          serverValidationName="reason"
          state={reasonState}
          placeholder="Välj anledning"
          required={true}
          hideRequiredAsterisk={true}
          options={unsubscribeReasons}
          onChange={(value: string) => setReasonState((ps) => ({ ...ps, value, validationInfo: new ValidationInfo() }))}
        />
        <HoneyPot ref={honeyPotRef} />
        <div className={`d-grid gap-2 d-md-block ${showErrorAlert ? 'mb-3' : ''}`}>
          <button type="button" className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick}>
            <span hidden={submitStatus === Status.loading}>Avsluta prenumeration</span>
          </button>
        </div>

        <Alert
          show={showErrorAlert}
          heading={TranslationService.get('stralkastarenunregistrationpage.failedsubmissionheadingtext')}
          headingTag="h2"
          content={TranslationService.get('stralkastarenunregistrationpage.failedsubmissionbodytext')}
          closable={false}
        />

        {TranslationService.get('stralkastarenunregistrationpage.unregisterorganizationtext') && (
          <>
            <hr />
            <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('stralkastarenunregistrationpage.unregisterorganizationtext')) }}></div>
          </>
        )}
      </div>

      {submitStatus === Status.success && (
        <div className="submit-successful__completed-info mb-4">
          <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('stralkastarenunregistrationpage.successtext').replace('#email#', emailState.value)) }}></div>
        </div>
      )}
    </div>
  );
}
export default StralkastarenUnregistration;